import type { Option, Selections, Type } from '@/pages/SpaceNotificatorPage/types'

import {
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField
} from '@mui/material'
import { filter, find, orderBy, remove, uniqBy } from 'lodash'
import { useState } from 'react'
import styled from 'styled-components'

import useNotificator from '@/pages/SpaceNotificatorPage/hooks/useNotificator'

const HelpButton = styled.button`
  border: none;
  align-self: end;
  padding: 0;
  cursor: pointer;
  margin: 0;
  text-align: center;

  color: ${({ theme }) => theme.notificator.wgColorModerate};
`

const FormSection = styled(TableCell)`
  border-bottom: none !important;
  padding: 10px !important;
  display: flex !important;
`

// Selection Type - how does the user want to select the card(s) to send
const TYPES = {
  ALL: 'all',
  INDIVIDUAL: 'individual',
  LOCATION: 'locations',
  DATA_SOURCE: 'dataSources',
  USE_CASE: 'useCases',
  PRESET: 'presets'
}

function Selectors(props: any) {
  const notificator = useNotificator()

  const data = notificator.metadata.data

  const imageLocation = `${
    process.env.REACT_APP_USE_DEV_ENV === 'true' ? 'https://workgridsoftware.dev.workgrid.com' : window.location.origin
  }/microapps/notificator/assets`

  // The option to just send all available cards
  const allCards = {
    name: `All ${data?.cards ? filter(data?.cards).length.toString() : '0'} Notifications`,
    key: TYPES.ALL,
    options: filter(data?.cards),
    cards: () => {
      return filter(data?.cards)
    }
  } as Type

  // The option to select the toolbar location (to do, to know) and send all cards that apply
  const location = {
    name: 'All Notifications of a Type...',
    key: TYPES.LOCATION,
    options: data?.locations || [],
    cards: (location: any) => {
      return filter(data?.cards, {
        location: find(data?.locations, { key: location }).code
      })
    }
  } as Type

  // The option to select a dataSource and send all cards available for that dataSource (ie, ServiceNow)
  const dataSources = {
    name: 'All Notifications from a Data Source...',
    key: TYPES.DATA_SOURCE,
    options: data?.dataSources || [],
    cards: (dataSource: any) => {
      return filter(data?.cards, (card: { existingIntegrations: Array<{ name: string; icon: string }> }) => {
        return card.existingIntegrations.find(
          integration => integration.name === find(data?.dataSources, { key: dataSource }).name
        )
      }).concat(
        filter(data?.cards, (card: { potentialIntegrations: Array<{ name: string; icon: string }> }) => {
          return card.potentialIntegrations.find(
            integration => integration.name === find(data?.dataSources, { key: dataSource }).name
          )
        })
      )
    }
  } as Type

  // The option to select a single card to send
  const individual = {
    name: 'An Individual Notification...',
    key: TYPES.INDIVIDUAL,
    options: filter(data?.cards || []),
    cards: (key: any) => {
      return filter(data?.cards, { key: key })
    }
  } as Type

  // The option to send all cards of a specific use case (ie, Expense Management)
  const useCase = {
    name: 'All Notifications for a Use Case...',
    key: TYPES.USE_CASE,
    options: data?.useCases || [],
    cards: (useCase: any) => {
      return filter(data?.cards, (card: { useCase: string }) => {
        return card.useCase.includes(find(data?.useCases, { key: useCase }).name)
      })
    }
  } as Type

  // The option to send all cards of a specific preset group (ie, HR Tech)
  const preset = {
    name: 'All Notifications from a Preset Group...',
    key: TYPES.PRESET,
    options: data?.presets || [],
    cards: (preset: any) => {
      return filter(data?.cards, (card: { presets: string | any[] }) => {
        return card.presets.includes(find(data?.presets, { key: preset }).name)
      })
    }
  } as Type

  // If we got any types that don't have any cards associated with them, remove those types from the dropdown list
  const typesForDropdown = remove([allCards, individual, dataSources, useCase, location, preset], (type: Type) => {
    return !type.options || type.options.length > 0
  })

  // Populates with appropriate values for secondary dropdown when type is selected
  const [cardsOptions, setCardsOptions] = useState<Array<Option>>()

  // Current selections
  const [selections, updateSelections] = useState({
    type: '',
    option: '',
    sourceName: '',
    sourceIcon: ''
  } as Selections)

  // Whether or not to show the source fields
  const [showSource, setShowSource] = useState(false)
  const [iconOptions, setIconOptions] = useState<Array<{ name: string; value: string }>>()

  // Update selected type - this will determine what values are available in the cards dropdown
  const _handleTypeChange = (event: { target: { value: string } }) => {
    const selectedTypeKey = event.target.value as string

    // Find the selected type object
    const type = find(typesForDropdown, { key: selectedTypeKey })

    // If this is the 'all' option, do not display option dropdown, but set selected to all cards
    if (selectedTypeKey === TYPES.ALL) {
      props.setSelected(type?.cards())
    } else {
      // Populate the cards dropdown with the options for the selected type and clear any previous selection
      setCardsOptions(type?.options)
      props.setSelected()
    }

    // Update the state with current selections
    updateSelections({
      type: selectedTypeKey,
      option: '',
      sourceName: '',
      sourceIcon: ''
    })

    // Source is not available with current selections, so hide it from view
    props.setSource({ sourceName: '', sourceIcon: '' })
    setShowSource(false)
  }

  // Update selected card in card dropdown
  const _handleOptionChange = (event: { target: { value: string } }) => {
    const value = event.target.value as string

    // Update meta for currently selected card(s) so we can show preview data
    const selected = find(typesForDropdown, { key: selections.type })?.cards(value)

    // Turn off customizing until we know it's available
    setShowSource(false)

    let sourceName = ''
    let sourceIcon = ''

    // If the user is selecting by Data Source, set that as the selected data source and don't allow customizing
    if (selections.type === TYPES.DATA_SOURCE) {
      const selectedDataSource = find(data?.dataSources, { key: value })

      props.setSource({
        sourceName: selectedDataSource.name,
        sourceIcon: selectedDataSource.icon
      })
    } else {
      // If all the currently selected cards have the same source, allow the user to customize it
      const sources = Array.from(new Set(selected?.map((item: any) => item.sourceName)))

      // Build the list of available icons
      if (sources.length === 1) {
        sourceName = sources[0] as string
        sourceIcon = selected[0].sourceIcon

        // Start with the generic default and then add the existing and potential options
        const options = [{ name: 'Default', value: sourceIcon, key: '' }]

        selected[0].existingIntegrations.forEach((integration: { name: string; icon: string }) => {
          options.push({
            name: integration.name + ' - Currently Available',
            value: integration.icon,
            key: integration.name
          })
        })

        selected[0].potentialIntegrations.forEach((integration: { name: string; icon: string }) => {
          options.push({
            name: integration.name + ' - Potentially Available',
            value: integration.icon,
            key: integration.name
          })
        })

        const sortedUniqueOptions = uniqBy(orderBy(options, ['key'], ['asc']), 'key')

        setIconOptions(sortedUniqueOptions)

        props.setSource({ sourceName, sourceIcon })
        setShowSource(true)
      }
    }

    props.setSelected(selected)

    // Set in state
    updateSelections(prevSelections => ({
      ...prevSelections,
      option: value,
      sourceName,
      sourceIcon: sourceIcon
    }))
  }

  // Update source value (only available when all selected cards have the same source
  const _handleSourceChange = (event: { target: { value: string; name: string } }) => {
    const value = event.target.value as string
    const name = event.target.name as string

    // Set in state
    updateSelections(prevSelections => ({
      ...prevSelections,
      [name]: value
    }))

    props.setSource({ [name]: value })
  }

  return (
    <tr>
      <td>
        <Table>
          <TableBody>
            <TableRow style={data?.cards && data.cards.length > 0 ? { display: 'table-row' } : { display: 'none' }}>
              <FormSection>
                <FormControl variant="standard" fullWidth>
                  <FormLabel>Send</FormLabel>
                  {notificator.metadata.status === 'loading' ? (
                    <div className="pt-3 pb-2 text-lg text-gray-700">Loading cards...</div>
                  ) : (
                    <Select
                      variant="standard"
                      aria-label="Send"
                      name="type"
                      value={selections.type}
                      onChange={_handleTypeChange}
                      data-testid={'select_type'}
                    >
                      {typesForDropdown.map((type: Type) => (
                        <MenuItem key={type.key} value={type.key}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>
              </FormSection>
            </TableRow>
            <TableRow
              style={
                selections.type === '' || selections.type === TYPES.ALL ? { display: 'none' } : { display: 'table-row' }
              }
            >
              <FormSection>
                <FormControl variant="standard" fullWidth>
                  <FormLabel>
                    {selections.type === 'dataSources'
                      ? 'Select Data Source'
                      : selections.type === 'individual'
                        ? 'Select Card'
                        : selections.type === 'locations'
                          ? 'Select Type'
                          : selections.type === 'presets'
                            ? 'Use Preset Group'
                            : selections.type === 'useCases'
                              ? 'Select Use Case'
                              : ''}
                  </FormLabel>
                  <Select
                    className="w-80"
                    variant="standard"
                    aria-label={
                      selections.type === 'dataSources'
                        ? 'Select Data Source'
                        : selections.type === 'individual'
                          ? 'Select Card'
                          : selections.type === 'locations'
                            ? 'Select Type'
                            : selections.type === 'presets'
                              ? 'Use Preset Group'
                              : selections.type === 'useCases'
                                ? 'Select Use Case'
                                : ''
                    }
                    name="card"
                    value={selections.option}
                    onChange={_handleOptionChange}
                    data-testid={'select_card'}
                  >
                    {cardsOptions?.map(option => (
                      <MenuItem key={option.key} value={option.key}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FormSection>
            </TableRow>
            <TableRow style={showSource ? { display: 'table-row' } : { display: 'none' }}>
              <FormSection>
                <FormControl variant="standard" fullWidth>
                  <FormLabel>Use Source Name</FormLabel>
                  <TextField
                    variant="standard"
                    aria-label="Use Source Name"
                    name="sourceName"
                    value={selections.sourceName}
                    onChange={_handleSourceChange}
                  />
                </FormControl>
              </FormSection>
            </TableRow>
            <TableRow style={showSource ? { display: 'table-row' } : { display: 'none' }}>
              <FormSection>
                <FormControl variant="standard" fullWidth>
                  <FormLabel>Use Source Icon</FormLabel>
                  <div className="flex justify-between space-x-2">
                    <Select
                      className="w-64"
                      variant="standard"
                      aria-label="Use Source Icon"
                      name="sourceIcon"
                      value={selections.sourceIcon}
                      onChange={_handleSourceChange}
                      data-testid={'select_sourceIcon'}
                    >
                      {iconOptions?.map(option => (
                        <MenuItem key={option.name} value={option.value}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <div className="flex w-16 items-center justify-center">
                      <img className="h-12 w-12" src={imageLocation + selections.sourceIcon} alt="source icon" />
                    </div>
                  </div>
                </FormControl>
              </FormSection>
            </TableRow>
          </TableBody>
        </Table>
      </td>
      <FormSection>
        <HelpButton name="help" type="button" onClick={() => props.onHelp('selectors')}>
          ?
        </HelpButton>
      </FormSection>
    </tr>
  )
}

export default Selectors
